import { useCallback, useRef, useEffect } from 'react'
import { trackEvent } from '@/lib/analytics'
import type { DemoState } from './demo-types'

// Dedup set for feature exploration tracking
const exploredFeatures = new Set<string>()

// Session start time
let sessionStartTime: number | null = null

function getTimeOnPage(): number {
  return sessionStartTime ? Math.round(performance.now() - sessionStartTime) : 0
}

export function useDemoAnalytics(state: DemoState) {
  const hasTrackedFirstSong = useRef(false)
  const prevPhase = useRef(state.phase)

  // Track page loaded on mount
  useEffect(() => {
    sessionStartTime = performance.now()
    trackEvent('demo_page_loaded')
  }, [])

  // Track phase transition to live
  useEffect(() => {
    if (state.phase === 'live' && prevPhase.current === 'pre-event') {
      trackEvent('demo_event_started', {
        time_to_start_ms: getTimeOnPage(),
      })
    }
    prevPhase.current = state.phase
  }, [state.phase])

  // Track first song played
  useEffect(() => {
    if (state.nowPlaying && !hasTrackedFirstSong.current) {
      hasTrackedFirstSong.current = true
      trackEvent('demo_first_song_played', {
        song_title: state.nowPlaying.song.title,
        time_since_start_ms: getTimeOnPage(),
      })
    }
  }, [state.nowPlaying])

  // Track feature usage (deduplicated)
  const trackFeatureUsed = useCallback((feature: string) => {
    if (!exploredFeatures.has(feature)) {
      exploredFeatures.add(feature)
      trackEvent('demo_feature_used', {
        feature,
        features_explored_count: exploredFeatures.size,
        time_on_page_ms: getTimeOnPage(),
      })
    }
  }, [])

  // Track coach mark events
  const trackCoachMarkShown = useCallback((markId: string) => {
    trackEvent('demo_coach_mark_shown', {
      mark_id: markId,
      time_on_page_ms: getTimeOnPage(),
    })
  }, [])

  const trackCoachMarkDismissed = useCallback((markId: string) => {
    trackEvent('demo_coach_mark_dismissed', {
      mark_id: markId,
      time_on_page_ms: getTimeOnPage(),
    })
  }, [])

  // Track CTA click
  const trackCtaClicked = useCallback((location: string) => {
    trackEvent('demo_cta_clicked', {
      location,
      features_explored_count: exploredFeatures.size,
      time_on_page_ms: getTimeOnPage(),
    })
  }, [])

  // Track phase time when leaving the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      trackEvent('demo_phase_time', {
        phase: state.phase,
        time_on_page_ms: getTimeOnPage(),
        features_explored_count: exploredFeatures.size,
      })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [state.phase])

  return {
    trackFeatureUsed,
    trackCoachMarkShown,
    trackCoachMarkDismissed,
    trackCtaClicked,
  }
}
